/**
 * Begriffe im Studio (Creator-Konto, Inhaber 22.10.2026, Runde 2): Navigation und Seitentitel sagen „Deals“, also heißen
 * Kooperationen im Studio überall so, in Deutsch („Deal“, „Deals“) und Englisch („deal“, „deals“). Das Agentur-Konto bleibt
 * bei „Kooperation“ bzw. „collaboration“.
 *
 * Die Sprachschicht sitzt in t() (lib/sprache.ts): Ist der Schalter Creator-Konto an, läuft jeder Oberflächentext hier durch.
 * Deutsch braucht dabei die Grammatik, weil „die Kooperation“ weiblich und „der Deal“ männlich ist:
 *   - Mehrzahl und Zusammensetzungen sind eindeutig (Kooperationen → Deals, Kooperationsvolumen → Deal-Volumen),
 *   - Einzahl nach Präposition mit Dativ bzw. Akkusativ (an der → am, zur → zum, für die → für den),
 *   - sonst „der/einer/dieser Kooperation“ als Genitiv (des/eines/dieses Deals),
 *   - „die/eine/diese/keine/jede Kooperation“ am Satzanfang oder nach einer Konjunktion als Nominativ (der/ein/dieser …),
 *     sonst als Akkusativ (den/einen/diesen …),
 *   - Adjektiv ohne Artikel (Neue Kooperation → Neuer Deal, 1 laufende Kooperation → 1 laufender Deal).
 * Namen von Vertragsvorlagen („Kooperationsvertrag“, „Collaboration agreement“) bleiben, sie sind Dokumentnamen.
 */

const SCHUTZ_DE = /Kooperationsvertr(?:ag(?:es|s)?|äge(?:n)?)/g;
const SCHUTZ_EN = /\b(?:[Cc]ollaboration|[Cc]ooperation) agreements?\b/g;

/** Präpositionen mit Dativ, die mit „dem“ verschmelzen */
const VERSCHMOLZEN: Record<string, string> = { an: "am", in: "im", bei: "beim", von: "vom", zu: "zum" };
const DATIV = ["an", "in", "bei", "von", "zu", "mit", "aus", "nach", "seit", "vor", "auf", "über", "unter", "neben", "zwischen", "gegenüber", "außer", "hinter", "entsprechend"];
const AKKUSATIV = ["für", "ohne", "um", "durch", "gegen", "pro", "je", "bis"];
/** Konjunktionen, nach denen meist das Subjekt folgt (Nominativ) */
const KONJUNKTION = ["wenn", "sobald", "bis", "ob", "dass", "weil", "da", "falls", "solange", "bevor", "nachdem", "wie", "sodass", "damit", "als", "wo", "sonst", "dann"];

const groß = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);
const wieVorlage = (vorlage: string, neu: string) => (/^[A-ZÄÖÜ]/.test(vorlage) ? groß(neu) : neu);

/** Adjektiv vor dem Nomen: -e (nach bestimmtem Artikel, Nominativ fem.) → passende Endung für „Deal“ */
const ADJ = "(?:\\s+([a-zäöüß]+(?:e|en)))?";

function deutsch(text: string): string {
  let s = text;
  // Zusammensetzungen (auch mit weichem Trennstrich): Kooperationsvolumen → Deal-Volumen
  s = s.replace(/\bKooperations(?:­)?([a-zäöüß]{3,})/g, (_m, rest: string) => `Deal-${groß(rest)}`);
  s = s.replace(/\bKooperationen\b/g, "Deals");
  // Zusammensetzungen mit „-kooperation“ (Folgekooperation → Folge-Deal); Titel wie „Beispielkooperation“ bleiben
  s = s.replace(/\b(Folge|Monats|Marken|Einzel)kooperation(en)?\b/g, (_m, vorn: string, mehr?: string) => `${vorn}-Deal${mehr ? "s" : ""}`);

  // Präposition mit Dativ + der/einer/dieser/jeder/keiner/deiner/ihrer (+ Adjektiv) + Kooperation
  const praep = DATIV.join("|");
  s = s.replace(new RegExp(`\\b(${praep})\\s+(der|einer|dieser|jeder|keiner|deiner|ihrer|seiner)${ADJ}\\s+Kooperation\\b`, "gi"), (m, p: string, art: string, adj: string | undefined) => {
    const a = art.toLowerCase();
    const adjektiv = adj ? ` ${adj.replace(/e$/, "en")}` : "";
    if (a === "der") {
      const v = VERSCHMOLZEN[p.toLowerCase()];
      return v ? `${wieVorlage(p, v)}${adjektiv} Deal` : `${p} dem${adjektiv} Deal`;
    }
    return `${p} ${a.replace(/er$/, "em")}${adjektiv} Deal`;
  });
  s = s.replace(new RegExp(`\\b(zur)${ADJ}\\s+Kooperation\\b`, "gi"), (_m, p: string, adj: string | undefined) => `${wieVorlage(p, "zum")}${adj ? ` ${adj.replace(/e$/, "en")}` : ""} Deal`);

  // Präposition mit Akkusativ + die/eine/diese/jede/keine/deine (+ Adjektiv) + Kooperation
  const akk = AKKUSATIV.join("|");
  s = s.replace(new RegExp(`\\b(${akk})\\s+(die|eine|diese|jede|keine|deine|ihre|seine)${ADJ}\\s+Kooperation\\b`, "gi"), (_m, p: string, art: string, adj: string | undefined) => {
    const a = art.toLowerCase();
    const neu = a === "die" ? "den" : a === "eine" ? "einen" : `${a.replace(/e$/, "")}en`;
    return `${p} ${neu}${adj ? ` ${adj.replace(/e$/, "en")}` : ""} Deal`;
  });

  // Genitiv: der/einer/dieser/jeder/deiner (+ Adjektiv) Kooperation → des/eines/dieses/jedes/deines … Deals
  s = s.replace(new RegExp(`\\b(der|einer|dieser|jeder|deiner|ihrer|seiner|keiner)${ADJ}\\s+Kooperation\\b`, "gi"), (_m, art: string, adj: string | undefined) => {
    const a = art.toLowerCase();
    const neu = a === "der" ? "des" : `${a.replace(/er$/, "")}es`;
    return `${wieVorlage(art, neu)}${adj ? ` ${adj.replace(/e$/, "en")}` : ""} Deals`;
  });

  // die/eine/diese/keine/jede/deine (+ Adjektiv) Kooperation: Nominativ am Satzanfang bzw. nach Konjunktion, sonst Akkusativ
  const konj = KONJUNKTION.join("|");
  s = s.replace(new RegExp(`(^|[.!?:;„“"(]\\s*|\\b(?:${konj})\\s+|\\s|,\\s*)(die|eine|diese|keine|jede|deine|ihre|seine)${ADJ}\\s+Kooperation\\b`, "gi"), (m, vor: string, art: string, adj: string | undefined) => {
    const a = art.toLowerCase();
    const satzanfang = /^[A-ZÄÖÜ]/.test(art);
    const nominativ = satzanfang || new RegExp(`\\b(?:${konj})\\s+$`, "i").test(vor) || /,\s*$/.test(vor);
    // ein/kein/dein/ihr/sein: im Nominativ ohne Endung, das Adjektiv trägt sie (ein laufender Deal)
    const unbestimmt = a === "eine" || a === "keine" || a === "deine" || a === "ihre" || a === "seine";
    const neu = nominativ
      ? (a === "die" ? "der" : unbestimmt ? a.replace(/e$/, "") : a.replace(/e$/, "er"))
      : (a === "die" ? "den" : `${a.replace(/e$/, "")}en`);
    const stamm = adj ? adj.replace(/en$|e$/, "") : "";
    const adjektiv = adj ? ` ${stamm}${nominativ ? (unbestimmt ? "er" : "e") : "en"}` : "";
    return `${vor}${satzanfang ? groß(neu) : neu}${adjektiv} Deal`;
  });

  // „Noch kein Deal“ statt „Noch keinen Deal“ (Leerzustand ohne Verb)
  s = s.replace(/\b(Noch|noch) keinen Deal\b/g, "$1 kein Deal");

  // Adjektiv ohne Artikel: „Neue Kooperation“ → „Neuer Deal“, „1 laufende Kooperation“ → „1 laufender Deal“
  s = s.replace(/(^|[\s(·„"])([A-Za-zÄÖÜäöüß]{3,})e\s+Kooperation\b/g, (m, vor: string, stamm: string) => {
    if (/^(di|dies|jed|kein|ein|dein|ihr|sein|ohn|ganz)$/i.test(stamm)) return m;
    return `${vor}${stamm}er Deal`;
  });

  return s.replace(/\bKooperation\b/g, "Deal");
}

function englisch(text: string): string {
  return text
    .replace(/\b([Cc])ollaborations\b|\b([Cc])ooperations\b/g, (_m, a?: string, b?: string) => ((a ?? b) === "C" ? "Deals" : "deals"))
    .replace(/\b([Cc])ollaboration\b|\b([Cc])ooperation\b/g, (_m, a?: string, b?: string) => ((a ?? b) === "C" ? "Deal" : "deal"));
}

/** Oberflächentext im Studio: Kooperation → Deal (Deutsch mit Grammatik), collaboration → deal. */
export function studioBegriffe(text: string, lang: "de" | "en"): string {
  if (!/Kooperation|ollaborat|ooperat/.test(text)) return text;
  const geschuetzt: string[] = [];
  const merke = (m: string) => `\u0000${geschuetzt.push(m) - 1}\u0000`;
  let s = text.replace(SCHUTZ_DE, merke).replace(SCHUTZ_EN, merke);
  s = deutsch(s);
  if (lang === "en" || /ollaborat|ooperat/.test(s)) s = englisch(s);
  return s.replace(/\u0000(\d+)\u0000/g, (_m, i: string) => geschuetzt[Number(i)]!);
}
